import React, { useState, useRef } from "react";
import {
  Box,
  Slider,
  Typography,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/system";
import { SkipPrevious, SkipNext } from "@mui/icons-material";
import ReactPlayer from "react-player";
import { MediaController } from "media-chrome/react";

interface PVLCVideoPlayerProps {
  url: string;
  isHost: boolean;
  controls?: boolean;
  isLarge: boolean;
  playing: boolean;
  playerRef: React.RefObject<any>;
  onPlay?: () => void;
  onPause?: () => void;
  onStop?: () => void;
  handleVideoProgress?: (progress: any) => void;
  handleVideoSeek?: (seek: any) => void;
  playbackRate?: number;
  handleVideoLoadedMetadata?: (e: any) => void;
  handleVideoTimeUpdate?: (e: any) => void;
  currentTime?: number;
  duration?: number;
  handleTimeLine?: (e: any, value: number | number[]) => void;
  onForward?: (seconds: number) => void;
  onBackward?: (seconds: number) => void;
  showProgressiveBar?: boolean;
}

const NLX = {
  surface: "#141414",
  overlay: "linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.75) 100%)",
  red: "#E50914",
  redHover: "#b20710",
  border: "rgba(255,255,255,0.14)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.74)",
};

/* ---- Styled bits for the Netflix feel ---- */
const Chrome = styled(Box)({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  background: NLX.overlay,
  padding: 12,
  pointerEvents: "auto",
});

const ControlsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 6,
  [theme.breakpoints.down("sm")]: {
    gap: 4,
    marginBottom: 4,
  },
}));

const TimeText = styled(Typography)(({ theme }) => ({
  color: NLX.text,
  fontSize: "0.8rem",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  minWidth: 88,
  textAlign: "right",
  marginLeft: "auto",
  [theme.breakpoints.down("sm")]: {
    minWidth: 72,
    fontSize: "0.75rem",
  },
}));

const NfxIconBtn = styled(IconButton)(({ theme }) => ({
  color: NLX.text,
  borderRadius: 10,
  backgroundColor: "transparent",
  transition: "background-color .15s ease, transform .12s ease",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.12)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
  },
  padding: 6,
  [theme.breakpoints.up("sm")]: { padding: 8 },
}));

const NfxSlider = styled(Slider)(({ theme }) => ({
  flex: 1,
  color: NLX.red,
  height: 4,
  cursor: "pointer",
  "& .MuiSlider-rail": {
    color: "rgba(255,255,255,0.28)",
    opacity: 1,
  },
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-thumb": {
    width: 12,
    height: 12,
    backgroundColor: NLX.red,
    boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
    transition: "width .12s ease, height .12s ease, box-shadow .12s ease",
    "&:hover, &.Mui-focusVisible": {
      boxShadow: "0 0 0 8px rgba(229,9,20,0.18)",
    },
    "&.Mui-active": {
      width: 16,
      height: 16,
    },
  },
  [theme.breakpoints.down("sm")]: {
    height: 3,
    "& .MuiSlider-thumb": {
      width: 10,
      height: 10,
      "&.Mui-active": { width: 12, height: 12 },
    },
  },
}));

const HoverTimeBubble = styled(Box)({
  position: "absolute",
  bottom: "100%",
  transform: "translateX(-50%)",
  backgroundColor: "rgba(0,0,0,0.86)",
  color: "#fff",
  padding: "4px 8px",
  borderRadius: 6,
  fontSize: "0.75rem",
  fontFamily:
    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  marginBottom: 8,
  pointerEvents: "none",
  zIndex: 10,
  boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
  border: `1px solid ${NLX.border}`,
  "&::after": {
    content: '""',
    position: "absolute",
    top: "100%",
    left: "50%",
    transform: "translateX(-50%)",
    border: "6px solid transparent",
    borderTopColor: "rgba(0,0,0,0.86)",
  },
});

/* ---- Component ---- */
const PVLCVideoPlayer: React.FC<PVLCVideoPlayerProps> = ({
  url,
  isHost,
  playerRef,
  controls = false,
  isLarge,
  playing,
  handleVideoTimeUpdate,
  onPlay,
  onPause,
  onStop,
  handleVideoProgress,
  handleVideoSeek,
  playbackRate,
  handleVideoLoadedMetadata,
  currentTime,
  duration,
  handleTimeLine,
  onForward,
  onBackward,
  showProgressiveBar = true,
}) => {
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<number>(0);
  const sliderRef = useRef<HTMLElement>(null);

  const isSmall = useMediaQuery("(max-width:600px)");

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
    // (Optional) add hours if needed
  };

  const progressValue =
    duration && currentTime ? (currentTime / duration) * 100 : 0;

  const handleSliderHover = (event: React.MouseEvent) => {
    if (!isHost || !duration || !sliderRef.current) return;
    const rect = (sliderRef.current as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const pct = Math.max(0, Math.min(x / rect.width, 1));
    const time = pct * duration;
    setHoverTime(Math.max(0, Math.min(time, duration)));
    setHoverPosition(x);
  };

  const handleSliderLeave = () => setHoverTime(null);

  const handleForward = () => {
    const jump = 10;
    const newTime = Math.min((currentTime || 0) + jump, duration || 0);
    onForward?.(jump);
    handleTimeLine?.(null, (newTime / (duration || 1)) * 100);
  };

  const handleBackward = () => {
    const jump = 10;
    const newTime = Math.max((currentTime || 0) - jump, 0);
    onBackward?.(jump);
    handleTimeLine?.(null, (newTime / (duration || 1)) * 100);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: isLarge ? "100%" : "auto",
        aspectRatio: isLarge ? "auto" : "16/9",
        borderRadius: 5,
        overflow: "hidden",
        background: NLX.surface,
        border: `1px solid ${NLX.border}`,
        boxShadow: "0 14px 38px rgba(0,0,0,0.5)",
      }}
    >
      <MediaController
        style={{
          width: "100%",
          aspectRatio: isLarge ? "auto" : "16/9",
          height: isLarge ? "100%" : "auto",
          pointerEvents: "none",
        }}
      >
        {/* ReactPlayer expects `url`, not `src` */}
        <ReactPlayer
          slot="media"
          src={url}
          ref={playerRef}
          playing={playing}
          style={{
            width: "100%",
            height: "100%",
          }}
          onPlay={() => {
            onPlay?.();
          }}
          onPause={() => {
            onPause?.();
          }}
          onLoadedMetadata={(e) => {
            handleVideoLoadedMetadata?.(e);
          }}
          onTimeUpdate={(e) => {
            handleVideoTimeUpdate?.(e);
          }}
          muted={!playing}
          controls={false}
          onProgress={(e) => {
            handleVideoProgress?.(e);
          }}
          onSeeked={(e) => {
            handleVideoSeek?.(e);
          }}
          playbackRate={playbackRate}
          autoPlay={true}
        />
      </MediaController>

      {/* Overlay controls */}
      <Chrome>
        <ControlsRow>
          {isHost && (
            <Tooltip placement="top" title="-10s" arrow>
              <NfxIconBtn onClick={handleBackward} size={isSmall ? "small" : "medium"}>
                <SkipPrevious fontSize={isSmall ? "small" : "medium"} />
              </NfxIconBtn>
            </Tooltip>
          )}

          {isHost && (
            <Tooltip placement="top" title="+10s" arrow>
              <NfxIconBtn onClick={handleForward} size={isSmall ? "small" : "medium"}>
                <SkipNext fontSize={isSmall ? "small" : "medium"} />
              </NfxIconBtn>
            </Tooltip>
          )}

          <TimeText variant="body2">
            {formatTime(currentTime || 0)} / {formatTime(duration || 0)}
          </TimeText>
        </ControlsRow>

        {/* Seek bar row */}
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          {hoverTime !== null && isHost && (
            <HoverTimeBubble sx={{ left: hoverPosition }}>
              {formatTime(hoverTime)}
            </HoverTimeBubble>
          )}

          {showProgressiveBar && (
            <NfxSlider
              ref={sliderRef}
              value={progressValue}
              onChange={isHost ? handleTimeLine : undefined}
              onMouseMove={isHost ? handleSliderHover : undefined}
              onMouseLeave={isHost ? handleSliderLeave : undefined}
              min={0}
              max={100}
              step={0.1}
              disabled={!isHost}
            />
          )}
        </Box>
      </Chrome>
    </Box>
  );
};

export default PVLCVideoPlayer;
